import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/shared/analytics'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'
import { bradleyFileTelemetryMeta } from '../analytics/bradley-analytics.telemetry.js'
import {
  BRADLEY_ATTACHMENT_STATUS,
  bradleyAttachmentAcceptAttribute,
  buildBradleyLocalAttachment,
  countBradleyActiveAttachments,
  createBradleyAttachmentClientId,
  getBradleyAttachmentPolicy,
  isBradleyImageFile,
  resolveBradleyImageFileName,
  validateBradleyAttachment,
} from '../utils/bradley-attachment.util.js'

function mapValidationToErrorType(validation) {
  return validation?.errorType || 'unknown'
}

function normalizeIncomingFile(file, uploadMethod, newFileUpload = false) {
  if (!isBradleyImageFile(file, newFileUpload)) {
    return file
  }

  const resolvedName = resolveBradleyImageFileName(
    file,
    uploadMethod === 'paste' ? 'pasted-image' : 'image',
  )

  if (resolvedName === file.name) {
    return file
  }

  return new File([file], resolvedName, { type: file.type || 'image/png' })
}

export function useBradleyComposerAttachments({
  documentUploadEnabled,
  newFileUploadEnabled = false,
  selectedDocumentsModel,
  emit,
  surface,
}) {
  const { t } = useI18n()
  const { trackEvent } = useAnalytics()

  const fileInput = ref(null)
  const isDragging = ref(false)
  const uploadErrors = ref([])
  const lastDropSignature = ref('')
  let lastDropAt = 0

  const newFileUpload = Boolean(newFileUploadEnabled)
  const attachmentPolicy = getBradleyAttachmentPolicy(newFileUpload)
  const acceptAttribute = bradleyAttachmentAcceptAttribute(newFileUpload)
  const currentFileCount = computed(() => countBradleyActiveAttachments(selectedDocumentsModel.value))

  function dismissUploadError(errorId = null) {
    if (errorId == null) {
      uploadErrors.value = []
      return
    }
    uploadErrors.value = uploadErrors.value.filter(error => error.id !== errorId)
  }

  function appendUploadError(error) {
    const id = error?.id || createBradleyAttachmentClientId()
    uploadErrors.value = [...uploadErrors.value, { ...error, id }]
    return id
  }

  function trackValidationFailure(file, uploadMethod, errorType) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
      upload_method: uploadMethod,
      error_type: errorType,
      file_count: 1,
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_FAIL, {
      ...bradleyFileTelemetryMeta(file),
      stage: 'client_validation',
    })
  }

  function pushInvalidAttachment(nextItems, file, validation, uploadMethod) {
    const errorItem = buildBradleyLocalAttachment(file, {
      uploadStatus: BRADLEY_ATTACHMENT_STATUS.ERROR,
      newFileUpload,
    })
    nextItems.push(errorItem)
    appendUploadError({
      id: errorItem.client_id,
      type: validation.errorType === 'file_size_limit' ? 'file_size_limit' : 'unsupported_format',
      fileName: file.name,
      fileSizeBytes: file.size,
    })
    trackValidationFailure(file, uploadMethod, mapValidationToErrorType(validation))
  }

  function pushFileCountLimitError(file, uploadMethod) {
    if (!uploadErrors.value.some(error => error.type === 'file_count_limit')) {
      appendUploadError({ type: 'file_count_limit' })
    }
    trackValidationFailure(file, uploadMethod, 'file_count_limit')
  }

  function queuePendingAttachment(nextItems, validFiles, file) {
    const pendingItem = buildBradleyLocalAttachment(file, {
      uploadStatus: BRADLEY_ATTACHMENT_STATUS.UPLOADING,
      newFileUpload,
    })
    nextItems.push(pendingItem)
    validFiles.push({ file, clientId: pendingItem.client_id })
  }

  function processSelectedFiles(fileList, uploadMethod) {
    if (!documentUploadEnabled) {
      return
    }

    const files = Array.from(fileList || []).filter(Boolean)
    if (!files.length) {
      return
    }

    const validFiles = []
    let projectedCount = currentFileCount.value
    let hadClientError = false
    const nextItems = [...(Array.isArray(selectedDocumentsModel.value) ? selectedDocumentsModel.value : [])]

    for (const rawFile of files) {
      const file = normalizeIncomingFile(rawFile, uploadMethod, newFileUpload)
      const validation = validateBradleyAttachment(file, newFileUpload)

      if (!validation.valid) {
        hadClientError = true
        pushInvalidAttachment(nextItems, file, validation, uploadMethod)
        continue
      }

      if (projectedCount >= attachmentPolicy.maxFiles) {
        hadClientError = true
        pushFileCountLimitError(file, uploadMethod)
        continue
      }

      projectedCount += 1
      queuePendingAttachment(nextItems, validFiles, file)
    }

    selectedDocumentsModel.value = nextItems

    if (!hadClientError) {
      dismissUploadError()
    }

    for (const entry of validFiles) {
      emit('fileUploaded', {
        file: entry.file,
        uploadMethod,
        clientId: entry.clientId,
      })
    }
  }

  function openFilePickerFromMenu() {
    if (!documentUploadEnabled) {
      return
    }

    trackEvent(BRADLEY_ANALYTICS_EVENTS.ADD_FILE_CLICKED, {
      source_component: 'chat_input',
      interaction_type: 'button_click',
      entry_point: 'plus_menu',
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SELECT_FILES, { surface })

    if (fileInput.value) {
      fileInput.value.value = ''
      fileInput.value.click()
    }
  }

  function handleFileSelected(event) {
    processSelectedFiles(event.target.files, 'button')
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }

  function handlePaste(event) {
    if (!documentUploadEnabled || !newFileUpload) {
      return
    }

    const clipboardItems = Array.from(event.clipboardData?.items || [])
    const imageFiles = clipboardItems
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean)

    if (!imageFiles.length) {
      return
    }

    event.preventDefault()
    processSelectedFiles(imageFiles, 'paste')
  }

  function handleDrop(event) {
    if (!documentUploadEnabled) {
      return
    }

    const files = event.dataTransfer?.files
    const now = Date.now()
    const signatureParts = files?.length
      ? Array.from(files).map(file => `${file.name}:${file.size}`)
      : []
    const signature = signatureParts.join('|')

    if (signature && signature === lastDropSignature.value && now - lastDropAt < 400) {
      isDragging.value = false
      return
    }

    lastDropSignature.value = signature
    lastDropAt = now

    if (files?.length) {
      const types = [...new Set(Array.from(files).map((file) => {
        const meta = bradleyFileTelemetryMeta(file)
        return meta.file_extension
      }))]

      trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_DRAG_AND_DROP, {
        source_component: 'chat_input',
        interaction_type: 'drag_and_drop',
        file_count: files.length,
        file_types: types,
      })
      trackEvent(BRADLEY_ANALYTICS_EVENTS.DRAG_AND_DROP_FILE, {
        surface,
        ...bradleyFileTelemetryMeta(files[0]),
      })

      processSelectedFiles(files, 'drag_and_drop')
    }

    isDragging.value = false
  }

  function handleDragOver() {
    isDragging.value = true
  }

  function handleDragLeave() {
    isDragging.value = false
  }

  function onComposerDragOver(event) {
    if (!documentUploadEnabled) {
      return
    }
    event.preventDefault()
    handleDragOver()
  }

  function onComposerDragLeave(event) {
    if (!documentUploadEnabled) {
      return
    }
    event.preventDefault()
    handleDragLeave()
  }

  function onComposerDrop(event) {
    if (!documentUploadEnabled) {
      return
    }
    event.preventDefault()
    handleDrop(event)
  }

  function removeSelectedUploadedDocument(index) {
    const next = [...selectedDocumentsModel.value]
    if (index < 0 || index >= next.length) {
      return
    }

    const removed = next[index]
    const previewUrl = removed?.preview_url || removed?.previewUrl
    if (previewUrl && typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl)
    }

    const removedClientId = removed?.client_id
    next.splice(index, 1)
    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_REMOVED, {
      source_component: 'chat_input',
      file_index: index,
      remaining_files: next.length,
    })
    selectedDocumentsModel.value = next

    if (removedClientId) {
      dismissUploadError(removedClientId)
    }

    if (countBradleyActiveAttachments(next) < attachmentPolicy.maxFiles) {
      uploadErrors.value = uploadErrors.value.filter(error => error.type !== 'file_count_limit')
    }
  }

  return {
    acceptAttribute,
    currentFileCount,
    dismissUploadError,
    fileInput,
    handleFileSelected,
    handlePaste,
    isDragging,
    onComposerDragLeave,
    onComposerDragOver,
    onComposerDrop,
    openFilePickerFromMenu,
    processSelectedFiles,
    removeSelectedUploadedDocument,
    t,
    uploadErrors,
  }
}
